import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    FaArrowLeft,
    FaBuilding,
    FaCheckCircle,
    FaChevronRight,
    FaClock,
    FaEdit,
    FaPowerOff,
    FaTasks,
    FaUserShield,
    FaUsers,
} from "react-icons/fa";

import Handler from "../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton } from "../../Components/Needed";
import DepartmentFormModal from "./DepartmentFormModal";
import Timeline from "../../Components/Timeline";
import { listBackPath } from "../../Helpers/useListParams";

const DepartmentDetail = () => {
    const { departmentId } = useParams();
    const navigate = useNavigate();

    const [department, setDepartment] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);

        const [detailResponse, logResponse] = await Promise.all([
            Handler({
                method: "GET",
                url: `/admin/staff/department/${departmentId}`,
            }),
            Handler({
                method: "GET",
                url: `/admin/staff/department/${departmentId}/log`,
                params: { limit: 20 },
            }),
        ]);

        setDepartment(
            detailResponse?.success
                ? detailResponse?.data?.data
                : null
        );

        setLogs(
            logResponse?.success
                ? logResponse?.data?.data || []
                : []
        );

        setLoading(false);
    }, [departmentId]);

    useEffect(() => {
        load();
    }, [load]);

    const toggleStatus = async () => {
        if (!department) return;

        setTogglingStatus(true);

        const response = await Handler({
            method: "patch",
            url: `/admin/staff/department/${departmentId}/status`,
            data: {
                status: !department.status,
            },
        });

        if (response?.success) {
            toast.success(
                response?.data?.message || "Status updated"
            );
            load();
        } else {
            toast.error(
                textCapitalize(
                    response?.data?.message ||
                    "Unable to update status"
                )
            );
        }

        setTogglingStatus(false);
    };

    if (loading) {
        return (
            <div className="container-fluid py-3">
                <Skeleton
                    height={20}
                    width="180px"
                    className="mb-4"
                />

                <div className="bg-white border rounded-4 p-4">
                    <Skeleton
                        height={32}
                        width="40%"
                        className="mb-3"
                    />
                    <Skeleton
                        height={18}
                        width="25%"
                        className="mb-4"
                    />
                    <Skeleton height={120} />
                </div>
            </div>
        );
    }

    if (!department) {
        return (
            <div className="container-fluid py-4">
                <div className="bg-white border rounded-4 text-center py-5 px-3">
                    <div
                        className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-light"
                        style={{
                            width: 64,
                            height: 64,
                        }}
                    >
                        <FaBuilding
                            className="text-muted"
                            size={24}
                        />
                    </div>

                    <h5 className="fw-semibold mb-2">
                        Department not found
                    </h5>

                    <p className="text-muted mb-4">
                        The department you're looking for
                        doesn't exist or may have been removed.
                    </p>

                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() =>
                            navigate(listBackPath("/departments"))
                        }
                    >
                        <FaArrowLeft className="me-2" />
                        Back to departments
                    </button>
                </div>
            </div>
        );
    }

    const taskTypes = department.taskTypes || [];
    const roles = department.roles || [];

    return (
        <>
            <div className="container-fluid py-3">

                {/* -------------------------------------------------
                    TOP NAVIGATION
                -------------------------------------------------- */}
                <div className="mb-3">
                    <button
                        type="button"
                        className="btn btn-link text-secondary text-decoration-none p-0 d-inline-flex align-items-center gap-2"
                        onClick={() =>
                            navigate(listBackPath("/departments"))
                        }
                    >
                        <FaArrowLeft size={13} />
                        <span>Back to departments</span>
                    </button>
                </div>


                {/* -------------------------------------------------
                    DEPARTMENT HEADER
                -------------------------------------------------- */}
                <div className="bg-white border rounded-4 shadow-sm mb-4 overflow-hidden">

                    <div className="p-4 p-lg-4">

                        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">

                            {/* Department identity */}
                            <div className="d-flex align-items-center gap-3">

                                <div
                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                    style={{
                                        width: 56,
                                        height: 56,
                                        background: "rgba(13, 110, 253, 0.08)",
                                    }}
                                >
                                    <FaBuilding
                                        size={23}
                                        className="text-primary"
                                    />
                                </div>

                                <div>
                                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">

                                        <h4 className="fw-semibold mb-0">
                                            {department.departmentName}
                                        </h4>

                                        <span
                                            className={`badge rounded-pill ${
                                                department.status
                                                    ? "text-bg-success"
                                                    : "text-bg-secondary"
                                            }`}
                                        >
                                            {department.status
                                                ? "Active"
                                                : "Inactive"}
                                        </span>
                                    </div>

                                    <div className="d-flex align-items-center flex-wrap gap-2 text-muted small">
                                        <span>
                                            Department ID:
                                        </span>

                                        <span className="font-monospace fw-medium text-dark">
                                            {department.departmentId}
                                        </span>
                                    </div>
                                </div>
                            </div>


                            {/* Actions */}
                            <div className="d-flex flex-wrap gap-2">

                                <button
                                    type="button"
                                    className="btn btn-light border d-inline-flex align-items-center gap-2"
                                    onClick={toggleStatus}
                                    disabled={togglingStatus}
                                >
                                    <FaPowerOff
                                        size={13}
                                        className={
                                            department.status
                                                ? "text-danger"
                                                : "text-success"
                                        }
                                    />

                                    {togglingStatus
                                        ? "Updating..."
                                        : department.status
                                            ? "Deactivate"
                                            : "Activate"}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary d-inline-flex align-items-center gap-2"
                                    onClick={() =>
                                        setShowEditModal(true)
                                    }
                                >
                                    <FaEdit size={13} />
                                    Edit department
                                </button>

                            </div>
                        </div>

                    </div>


                    {/* Quick statistics */}
                    <div className="border-top bg-light bg-opacity-50">

                        <div className="row g-0">

                            <div className="col-6 col-md-4">
                                <div className="px-4 py-3 border-end">
                                    <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                                        <FaTasks size={12} />
                                        Task types
                                    </div>

                                    <div className="fw-semibold">
                                        {taskTypes.length}
                                    </div>
                                </div>
                            </div>

                            <div className="col-6 col-md-4">
                                <div className="px-4 py-3 border-end">
                                    <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                                        <FaUserShield size={12} />
                                        Roles
                                    </div>

                                    <div className="fw-semibold">
                                        {roles.length}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-md-4">
                                <div className="px-4 py-3">
                                    <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                                        <FaClock size={12} />
                                        Last updated
                                    </div>

                                    <div className="fw-medium small">
                                        {formatDateTime(
                                            department.updatedAt
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>


                {/* -------------------------------------------------
                    MAIN CONTENT
                -------------------------------------------------- */}
                <div className="row g-4">

                    {/* LEFT CONTENT */}
                    <div className="col-12 col-xl-7">

                        {/* Overview */}
                        <div className="bg-white border rounded-4 shadow-sm mb-4">

                            <div className="p-4 border-bottom">
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-2 bg-primary bg-opacity-10 text-primary"
                                        style={{
                                            width: 36,
                                            height: 36,
                                        }}
                                    >
                                        <FaBuilding size={15} />
                                    </div>

                                    <div>
                                        <h6 className="fw-semibold mb-0">
                                            Department overview
                                        </h6>

                                        <small className="text-muted">
                                            Basic department information
                                        </small>
                                    </div>
                                </div>
                            </div>


                            <div className="p-4">

                                {/* Description */}
                                {department.description && (
                                    <div className="mb-4">
                                        <label className="text-muted small fw-medium d-block mb-2">
                                            Description
                                        </label>

                                        <div className="bg-light rounded-3 p-3 text-secondary">
                                            {department.description}
                                        </div>
                                    </div>
                                )}


                                {/* Task Types */}
                                <div className="mb-4">
                                    <label className="text-muted small fw-medium d-block mb-2">
                                        Task types
                                    </label>

                                    {taskTypes.length ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {taskTypes.map(
                                                (task) => (
                                                    <span
                                                        key={task}
                                                        className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10 px-3 py-2 text-capitalize"
                                                    >
                                                        <FaCheckCircle
                                                            size={11}
                                                            className="me-1"
                                                        />
                                                        {task}
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-muted small">
                                            No task types assigned
                                        </span>
                                    )}
                                </div>


                                {/* Dates */}
                                <div className="row g-3">

                                    <div className="col-12 col-md-6">
                                        <div className="border rounded-3 p-3 h-100">
                                            <div className="text-muted small mb-1">
                                                Created
                                            </div>

                                            <div className="fw-medium small">
                                                {formatDateTime(
                                                    department.createdAt
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="border rounded-3 p-3 h-100">
                                            <div className="text-muted small mb-1">
                                                Last updated
                                            </div>

                                            <div className="fw-medium small">
                                                {formatDateTime(
                                                    department.updatedAt
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                </div>

                            </div>
                        </div>


                        {/* Roles */}
                        <div className="bg-white border rounded-4 shadow-sm">

                            <div className="p-4 border-bottom">

                                <div className="d-flex align-items-center justify-content-between gap-3">

                                    <div className="d-flex align-items-center gap-2">

                                        <div
                                            className="d-flex align-items-center justify-content-center rounded-2 bg-primary bg-opacity-10 text-primary"
                                            style={{
                                                width: 36,
                                                height: 36,
                                            }}
                                        >
                                            <FaUsers size={15} />
                                        </div>

                                        <div>
                                            <h6 className="fw-semibold mb-0">
                                                Roles
                                            </h6>

                                            <small className="text-muted">
                                                Roles assigned to this department
                                            </small>
                                        </div>

                                    </div>

                                    <span className="badge text-bg-light border rounded-pill">
                                        {roles.length}
                                    </span>

                                </div>
                            </div>


                            <div className="p-3">

                                {roles.length ? (
                                    <div className="d-flex flex-column">

                                        {roles.map(
                                            (role, index) => (
                                                <button
                                                    key={
                                                        role.roleId
                                                    }
                                                    type="button"
                                                    className={`btn btn-link text-decoration-none text-start px-3 py-3 ${
                                                        index !==
                                                        roles.length -
                                                        1
                                                            ? "border-bottom"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        navigate(
                                                            `/roles/${role.roleId}`
                                                        )
                                                    }
                                                >
                                                    <div className="d-flex align-items-center justify-content-between gap-3">

                                                        <div className="d-flex align-items-center gap-3">

                                                            <div
                                                                className="d-flex align-items-center justify-content-center rounded-3 bg-light text-secondary flex-shrink-0"
                                                                style={{
                                                                    width: 40,
                                                                    height: 40,
                                                                }}
                                                            >
                                                                <FaUserShield
                                                                    size={
                                                                        16
                                                                    }
                                                                />
                                                            </div>

                                                            <div>
                                                                <div className="fw-semibold text-dark">
                                                                    {
                                                                        role.roleName
                                                                    }
                                                                </div>

                                                                <div className="small text-muted">
                                                                    {
                                                                        role.roleId
                                                                    }
                                                                </div>
                                                            </div>

                                                        </div>

                                                        <div className="d-flex align-items-center gap-3">

                                                            <span
                                                                className={`badge rounded-pill ${
                                                                    role.status
                                                                        ? "text-bg-success"
                                                                        : "text-bg-secondary"
                                                                }`}
                                                            >
                                                                {role.status
                                                                    ? "Active"
                                                                    : "Inactive"}
                                                            </span>

                                                            <FaChevronRight
                                                                size={
                                                                    12
                                                                }
                                                                className="text-muted"
                                                            />

                                                        </div>

                                                    </div>
                                                </button>
                                            )
                                        )}

                                    </div>
                                ) : (
                                    <div className="text-center py-5 px-3">

                                        <div
                                            className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-light text-muted"
                                            style={{
                                                width: 52,
                                                height: 52,
                                            }}
                                        >
                                            <FaUsers size={19} />
                                        </div>

                                        <h6 className="fw-semibold mb-1">
                                            No roles yet
                                        </h6>

                                        <p className="text-muted small mb-0">
                                            No roles have been created
                                            for this department.
                                        </p>

                                    </div>
                                )}

                            </div>
                        </div>

                    </div>


                    {/* RIGHT CONTENT */}
                    <div className="col-12 col-xl-5">

                        <div className="bg-white border rounded-4 shadow-sm overflow-hidden sticky-xl-top"
                            style={{
                                top: "1rem",
                            }}
                        >

                            <div className="p-4 border-bottom">

                                <div className="d-flex align-items-center gap-2">

                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-2 bg-primary bg-opacity-10 text-primary"
                                        style={{
                                            width: 36,
                                            height: 36,
                                        }}
                                    >
                                        <FaClock size={15} />
                                    </div>

                                    <div>
                                        <h6 className="fw-semibold mb-0">
                                            Activity
                                        </h6>

                                        <small className="text-muted">
                                            Recent department changes
                                        </small>
                                    </div>

                                </div>

                            </div>

                            <div className="p-3">
                                <Timeline userLogs={logs} />
                            </div>

                        </div>

                    </div>

                </div>
            </div>


            {/* -------------------------------------------------
                EDIT MODAL
            -------------------------------------------------- */}
            {showEditModal && (
                <DepartmentFormModal
                    mode="edit"
                    department={department}
                    onClose={() =>
                        setShowEditModal(false)
                    }
                    onSaved={() => {
                        setShowEditModal(false);
                        load();
                    }}
                />
            )}
        </>
    );
};

export default DepartmentDetail;
